// src/pages/ChatWithAI/index.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useClient } from '../../contexts/ClientContext';
import { useClientLogContext } from '../../hooks/useClientLogContext';
import { startPreventiveRefresh } from './utils/chatAuthHelper';
import ChatMessage from './ChatMessage';
import VerificationDialog from './VerificationDialog';
import ThreadSidebar from './components/ThreadSidebar';
import { useChatWithTools } from './hooks/useChatWithTools';
import { useThreadHistory } from './hooks/useThreadHistory';
import './ChatWithAI.css';
import { useAttachments } from './hooks/useAttachments';
import AttachmentBox from './components/AttachmentBox';
import AgentRunDialog from '../../components/dialogs/AgentRunDialog';

const DEFAULT_SYSTEM_PROMPT = `Instruções: Atue como um advogado português, colega de escritório, num contexto profissional e formal. O seu nome é "Robot JR" e faz parte da equipa da sociedade "Jaime Roriz Advogados" em Lisboa. A comunicação deve ser sempre feita em Português de Portugal e com recurso a linguagem jurídica e terminologia adequada à área do Direito. Mantenha um tom profissional, direto e objetivo, como seria de esperar de um colega. Sempre que possível, refira-se ao utilizador como "colega".`;

const STORAGE_KEY_LAST_MODEL = 'infinity_chat_last_model';
const STORAGE_KEY_REASONING_MODE = 'infinity_chat_reasoning_mode';
const STORAGE_KEY_WEB_SEARCH = 'infinity_chat_web_search';
const STORAGE_KEY_VERBOSITY = 'infinity_chat_verbosity';

const saveLastModelToStorage = (modelId) => {
  try {
    if (modelId && typeof modelId === 'string') {
      localStorage.setItem(STORAGE_KEY_LAST_MODEL, modelId);
      console.log('💾 Modelo salvo no localStorage:', modelId);
    }
  } catch (error) {
    console.warn('⚠️ Erro ao salvar modelo no localStorage:', error.message);
  }
};

const getLastModelFromStorage = () => {
  try {
    const savedModelId = localStorage.getItem(STORAGE_KEY_LAST_MODEL);
    if (savedModelId) {
      console.log('📖 Modelo recuperado do localStorage:', savedModelId);
      return savedModelId;
    }
  } catch (error) {
    console.warn('⚠️ Erro ao ler modelo do localStorage:', error.message);
  }
  return null;
};

const saveReasoningModeToStorage = (mode) => {
  try {
    if (mode && ['fast', 'deep'].includes(mode)) {
      localStorage.setItem(STORAGE_KEY_REASONING_MODE, mode);
      console.log('💾 Modo de raciocínio salvo:', mode);
    }
  } catch (error) {
    console.warn('⚠️ Erro ao salvar modo de raciocínio:', error.message);
  }
};

const getReasoningModeFromStorage = () => {
  try {
    const savedMode = localStorage.getItem(STORAGE_KEY_REASONING_MODE);
    if (savedMode && ['fast', 'deep'].includes(savedMode)) {
      console.log('📖 Modo de raciocínio recuperado:', savedMode);
      return savedMode;
    }
  } catch (error) {
    console.warn('⚠️ Erro ao ler modo de raciocínio:', error.message);
  }
  return 'fast';
};

const saveWebSearchToStorage = (enabled) => {
  try {
    localStorage.setItem(STORAGE_KEY_WEB_SEARCH, enabled ? 'true' : 'false');
    console.log('💾 Web Search salvo:', enabled);
  } catch (error) {
    console.warn('⚠️ Erro ao salvar web search:', error.message);
  }
};

const getWebSearchFromStorage = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY_WEB_SEARCH);
    return saved === 'true';
  } catch (error) {
    console.warn('⚠️ Erro ao ler web search:', error.message);
  }
  return false;
};

const saveVerbosityToStorage = (enabled) => {
  try {
    localStorage.setItem(STORAGE_KEY_VERBOSITY, enabled ? 'true' : 'false');
    console.log('💾 Respostas Longas (verbosity) salvo:', enabled);
  } catch (error) {
    console.warn('⚠️ Erro ao salvar verbosity:', error.message);
  }
};

// ✅ Default TRUE: só volta a 'false' se o utilizador explicitamente desligou antes
const getVerbosityFromStorage = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY_VERBOSITY);
    if (saved === null) return true;
    return saved === 'true';
  } catch (error) {
    console.warn('⚠️ Erro ao ler verbosity:', error.message);
  }
  return true;
};

const isReasoningSupported = (modelId) => {
  if (!modelId) return false;
  const lower = modelId.toLowerCase();

  const isGPT5 = lower.includes('gpt-5') ||
                 lower.startsWith('o1') ||
                 lower.startsWith('o3') ||
                 lower.startsWith('o4');

  const isGemini3Plus = lower.includes('gemini-3') ||
                        lower.includes('gemini-4') ||
                        lower.includes('gemini-2.5');

  const isClaude = lower.startsWith('claude-');

  return isGPT5 || isGemini3Plus || isClaude;
};

const isWebSearchSupported = (modelId) => {
  if (!modelId) return false;
  const lower = modelId.toLowerCase();

  const isGPT5Plus = lower.includes('gpt-5') ||
                     lower.startsWith('o1') ||
                     lower.startsWith('o3') ||
                     lower.startsWith('o4');

  const isGemini3Plus = lower.includes('gemini-3') ||
                        lower.includes('gemini-4') ||
                        lower.includes('gemini-2.5');

  const isClaude = lower.startsWith('claude-');

  return isGPT5Plus || isGemini3Plus || isClaude;
};

// ✅ Verbosity (Respostas Longas) é um parâmetro específico da API da OpenAI para GPT-5+
const isVerbositySupported = (modelId) => {
  if (!modelId) return false;
  const lower = modelId.toLowerCase();
  return lower.includes('gpt-5');
};

const ChatWithAI = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshAccessToken } = useAuth();
  const { clienteAtual } = useClient();
  // Cliente/processo a que o consumo de IA desta conversa é atribuído (chat_log)
  const { codCli, codProcesso } = useClientLogContext();

  const messagesEndRef = useRef(null);
  const messagesAreaRef = useRef(null);
  const lastScrollTime = useRef(0);
  const inputRef = useRef(null);
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentThreadId, setCurrentThreadId] = useState(null);
  
  const [contextContent, setContextContent] = useState('');
  const [isContextCollapsed, setIsContextCollapsed] = useState(true);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState(DEFAULT_SYSTEM_PROMPT);
  const [isSystemPromptCollapsed, setIsSystemPromptCollapsed] = useState(true);
  const [systemPromptSaving, setSystemPromptSaving] = useState(false);
  const [systemPromptStatus, setSystemPromptStatus] = useState(null);
  
  const [reasoningMode, setReasoningMode] = useState('fast');
  const [useWebSearch, setUseWebSearch] = useState(false);
  const [useVerbosity, setUseVerbosity] = useState(true);
  
  const [useGroundSearch, setUseGroundSearch] = useState(false);
  const [useThinking, setUseThinking] = useState(false);
  const [useTools, setUseTools] = useState(false);
  const [originInfo, setOriginInfo] = useState(null);
  const [casoId, setCasoId] = useState(null);
  
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [verificationData, setVerificationData] = useState(null);
  const [verificationCache, setVerificationCache] = useState(new Map());

  const threadHistory = useThreadHistory(refreshAccessToken);

  const [attachmentBoxOpen, setAttachmentBoxOpen] = useState(false);
  const [agentRunOpen, setAgentRunOpen] = useState(false);
  const [baseContextContent, setBaseContextContent] = useState('');

  // ✅ Hook de anexos com callback para criar thread
  const attachments = useAttachments(currentThreadId, (newThreadId) => {
    console.log('📝 Thread criada automaticamente para anexo:', newThreadId);
    setCurrentThreadId(newThreadId);
    setThreadId(newThreadId);
    localStorage.setItem('currentThreadId', newThreadId.toString());
  }, { codCli, codProcesso });



  useEffect(() => {
    if (location.state?.content) {
      setBaseContextContent(location.state.content);
    }
  }, [location.state]);

  const fullContextContent = useMemo(() => {
    const anexosContext = attachments.getContextoAnexos();
    
    if (baseContextContent && anexosContext) {
      return `${baseContextContent}\n${anexosContext}`;
    }
    
    return baseContextContent || anexosContext;
  }, [baseContextContent, attachments]);

  const {
    messages,
    input,
    isLoading,
    error: chatError,
    handleInputChange,
    handleSubmit,
    setMessages,
    setThreadId,
    hasActiveTools,    
    toolResults        
  } = useChatWithTools({
    model: selectedModel?.id || 'gpt-4',
    systemPrompt: systemPrompt,
    contextContent: fullContextContent,
    useTools: useTools,
    casoId: casoId,
    codCli: codCli,
    codProcesso: codProcesso,
    threadId: currentThreadId,
    reasoningMode: reasoningMode,
    useWebSearch: useWebSearch,
    useVerbosity: useVerbosity,
    refreshAccessToken: refreshAccessToken,
    onThreadCreated: (newThreadId) => {
      console.log('🆕 Thread criada pelo chat:', newThreadId);
      setCurrentThreadId(newThreadId);
      localStorage.setItem('currentThreadId', newThreadId.toString());
    }
  });

  // ✅ Auto-grow textarea (até 35% da altura da janela)
  const autoResizeTextarea = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;

    el.style.height = 'auto';

    const max = Math.floor(window.innerHeight * 0.35);
    const next = Math.min(el.scrollHeight, max);

    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > max ? 'auto' : 'hidden';
  }, []);

  useEffect(() => {
    autoResizeTextarea(); // cresce e também "encolhe" quando apaga/limpa
  }, [input, autoResizeTextarea]);

  useEffect(() => {
    const onResize = () => autoResizeTextarea();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [autoResizeTextarea]);


  // ── Agente: formatar contexto da conversa para o preflight ──────────────
  const buildAgentContext = useCallback(() => {
    const conversationText = messages
      .map(m => `[${m.role === 'user' ? 'Utilizador' : 'Assistente'}]:\n${m.content || ''}`)
      .join('\n\n');
    return [fullContextContent, conversationText].filter(Boolean).join('\n\n---\n\n');
  }, [messages, fullContextContent]);

  const handleAgentResult = useCallback((content, isFile, fileName) => {
    const messageContent = isFile
      ? `**Ficheiro gerado pelo agente:** ${fileName}\n*(utilize o link de download no dialog para guardar o ficheiro)*`
      : content;
    setMessages(prev => [
      ...prev,
      { role: 'assistant', content: messageContent, timestamp: new Date() },
    ]);
  }, [setMessages]);

  // ✅ Handlers de anexos COM garantia de thread
  const handleAnexarTextoTranscrito = async (anexos) => {
    try {
      for (const { nome, conteudo } of anexos) {
        await attachments.anexarTextoTranscrito({ nome, conteudo });
      }
      alert(`${anexos.length} ficheiro(s) anexado(s) com sucesso!`);
    } catch (error) {
      alert(error.message);
    }
  };

  const handleAnexarDocumento = async (documentoId) => {
    try {
      await attachments.anexarDocumento(documentoId);
      alert('Documento anexado com sucesso!');
    } catch (error) {
      alert(error.message);
    }
  };

  const handleAnexarCaso = async (casoId) => {
    try {
      await attachments.anexarCaso(casoId);
      alert('Caso anexado com sucesso!');
    } catch (error) {
      alert(error.message);
    }
  };

  const handleAnexarCliente = async (clientCode) => {
    try {
      await attachments.anexarCliente(clientCode);
      alert('Cliente anexado com sucesso!');
    } catch (error) {
      alert(error.message);
    }
  };


  useEffect(() => {
    const savedMode = getReasoningModeFromStorage();
    setReasoningMode(savedMode);
    
    const savedWebSearch = getWebSearchFromStorage();
    setUseWebSearch(savedWebSearch);

    const savedVerbosity = getVerbosityFromStorage();
    setUseVerbosity(savedVerbosity);
  }, []);

  useEffect(() => {
    console.log('🆕 Chat montado: Limpando thread anterior');
    localStorage.removeItem('currentThreadId');
    setCurrentThreadId(null);
    setThreadId(null);
    setMessages([]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    console.log(`🔍 [${new Date().toISOString()}] isLoading mudou para:`, isLoading);
  }, [isLoading]);

  useEffect(() => {
    console.log(`🔍 [${new Date().toISOString()}] messages.length mudou para:`, messages.length);
  }, [messages.length]);

  useEffect(() => {
    console.log('🔐 Chat: Iniciando renovação automática de token');
    const stopRefresh = startPreventiveRefresh(refreshAccessToken);
    return () => {
      console.log('🛑 Chat: Parando renovação automática');
      stopRefresh();
    };
  }, [refreshAccessToken]);

  // Corrige cópia: remove estilos dark e preserva markdown renderizado (HTML)
  useEffect(() => {
    const el = messagesAreaRef.current;
    if (!el) return;

    const handleCopy = (e) => {
      const selection = window.getSelection();
      if (!selection || !selection.rangeCount || !selection.toString().trim()) return;

      const range = selection.getRangeAt(0);
      const fragment = range.cloneContents();
      const container = document.createElement('div');
      container.appendChild(fragment);

      // Remove cores dark; força texto preto sobre branco para colar no Word/Outlook
      container.querySelectorAll('*').forEach((node) => {
        node.style.removeProperty('background-color');
        node.style.removeProperty('background');
        node.style.removeProperty('color');
      });

      e.preventDefault();
      e.clipboardData.setData(
        'text/html',
        `<div style="font-family:Arial,sans-serif;color:#000000;background:#ffffff;">${container.innerHTML}</div>`
      );
      e.clipboardData.setData('text/plain', selection.toString());
    };

    el.addEventListener('copy', handleCopy);
    return () => el.removeEventListener('copy', handleCopy);
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      axios.defaults.baseURL = 'http://localhost:5000';
    }
  }, []);

  useEffect(() => {
    if (currentThreadId) {
      console.log(`💾 Thread ID atualizado: ${currentThreadId}`);
      localStorage.setItem('currentThreadId', currentThreadId.toString());
    }
  }, [currentThreadId]);

  useEffect(() => {
    if (location.state?.content) {
      setContextContent(location.state.content);
    }
    if (location.state?.origin) {
      setOriginInfo({
        origin: location.state.origin,
        title: location.state.originTitle || 'Página Anterior'
      });
    }
    if (location.state?.casoId) {
      setCasoId(location.state.casoId);
    }
  }, [location.state]);

  useEffect(() => {
    fetchModels();
  }, []);

  // ✅ Prompt de sistema gravado pelo utilizador. Sem linha na BD fica o padrão,
  // que é o comportamento que o ecrã sempre teve — por isso uma falha aqui só
  // avisa no log e não bloqueia o chat.
  useEffect(() => {
    let activo = true;

    const fetchSystemPrompt = async () => {
      try {
        const response = await axios.get('/api/chatai/system-prompt');
        if (activo && response.data?.success && response.data.data?.texto) {
          setSystemPrompt(response.data.data.texto);
          console.log('📖 Prompt de sistema personalizado carregado');
        }
      } catch (error) {
        console.warn('⚠️ Erro ao carregar prompt de sistema:', error.message);
      }
    };

    fetchSystemPrompt();
    return () => { activo = false; };
  }, []);

  // Mensagem de gravação some sozinha para não ficar presa no painel
  useEffect(() => {
    if (!systemPromptStatus) return;
    const timer = setTimeout(() => setSystemPromptStatus(null), 4000);
    return () => clearTimeout(timer);
  }, [systemPromptStatus]);

  useEffect(() => {
    if (selectedModel) {
      const modelIdLower = (selectedModel.id || '').toLowerCase();
      const modelProviderLower = (selectedModel.provider || '').toLowerCase();
      
      const isGoogleModel = modelProviderLower === 'google' || 
                           modelIdLower.includes('gemini') || 
                           modelIdLower.includes('bard');
      
      setUseGroundSearch(isGoogleModel);
      setUseThinking(isGoogleModel);
    }
  }, [selectedModel]);

  const scrollToBottom = useCallback((force = false) => {
    if (!messagesEndRef.current) return;
    
    const now = Date.now();
    const timeSinceLastScroll = now - lastScrollTime.current;
    
    if (isLoading) {
      if (!force && timeSinceLastScroll < 500) return;
      messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
    } else {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
    
    lastScrollTime.current = now;
  }, [isLoading]);

  useEffect(() => {
    if (messages.length > 0) {
      const timer = setTimeout(() => scrollToBottom(), 100);
      return () => clearTimeout(timer);
    }
  }, [messages.length, scrollToBottom]);

  useEffect(() => {
    if (!isLoading && messages.length > 0) {
      setTimeout(() => scrollToBottom(true), 200);
    }
  }, [isLoading, messages.length, scrollToBottom]);

  const fetchModels = async () => {
    try {
      const response = await axios.get('/api/chat/models');
      if (response.data.success) {
        const availableModels = response.data.data;
        setModels(availableModels);
        
        if (availableModels.length > 0) {
          const lastModelId = getLastModelFromStorage();
          let modelToSelect = null;

          if (lastModelId) {
            modelToSelect = availableModels.find(m => m.id === lastModelId);
            
            if (modelToSelect) {
              console.log('✅ Usando último modelo salvo:', lastModelId);
            } else {
              console.log('⚠️ Modelo salvo não encontrado, usando primeiro da lista');
            }
          }

          if (!modelToSelect) {
            modelToSelect = availableModels[0];
          }

          setSelectedModel(modelToSelect);
        }
      }
    } catch (error) {
      console.error('Error fetching models:', error);
    }
  };

  const handleModelChange = useCallback((e) => {
    const modelId = e.target.value;
    const model = models.find(m => m.id === modelId);
    
    if (model) {
      setSelectedModel(model);
      saveLastModelToStorage(model.id);
    }
  }, [models]);

  // ✅ Grava o prompt de sistema deste utilizador. Textarea vazia repõe o padrão.
  const handleSaveSystemPrompt = useCallback(async () => {
    setSystemPromptSaving(true);
    setSystemPromptStatus(null);

    try {
      const response = await axios.put('/api/chatai/system-prompt', { texto: systemPrompt });
      setSystemPromptStatus({
        tipo: 'ok',
        texto: response.data?.message || 'Prompt gravado.'
      });
    } catch (error) {
      console.error('❌ Erro ao gravar prompt de sistema:', error);
      setSystemPromptStatus({
        tipo: 'erro',
        texto: error.response?.data?.message || 'Não foi possível gravar o prompt.'
      });
    } finally {
      setSystemPromptSaving(false);
    }
  }, [systemPrompt]);

  const handleReasoningModeChange = useCallback((mode) => {
    setReasoningMode(mode);
    saveReasoningModeToStorage(mode);
    console.log('🧠 Modo de raciocínio alterado para:', mode);
  }, []);

  const handleWebSearchChange = useCallback((e) => {
    const enabled = e.target.checked;
    setUseWebSearch(enabled);
    saveWebSearchToStorage(enabled);
    console.log('🌐 Web Search alterado para:', enabled);
  }, []);

  const handleVerbosityChange = useCallback((e) => {
    const enabled = e.target.checked;
    setUseVerbosity(enabled);
    saveVerbosityToStorage(enabled);
    console.log('📝 Respostas Longas (verbosity) alterado para:', enabled);
  }, []);

  const handleSendMessage = (e) => {
    e?.preventDefault();
    console.log(`📨 [${new Date().toISOString()}] handleSendMessage INÍCIO`);
  
    if (!input.trim() || !selectedModel || isLoading) {
      console.log(`⚠️ [${new Date().toISOString()}] handleSendMessage BLOQUEADO (validação falhou)`);
      return;
    }

    handleSubmit(e);
    console.log(`✅ [${new Date().toISOString()}] handleSendMessage SUBMIT CHAMADO`);
    
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
      console.log(`🎯 [${new Date().toISOString()}] handleSendMessage COMPLETE`);
    }, 100);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const handleVerifyResponse = useCallback((messageIndex) => {
    const cachedVerification = verificationCache.get(messageIndex);
    
    if (cachedVerification) {
      setVerificationData({
        ...cachedVerification,
        fromCache: true
      });
      setShowVerificationDialog(true);
      return;
    }

    let userQuestion = '';
    for (let i = messageIndex - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        userQuestion = messages[i].content;
        break;
      }
    }

    const llmResponse = messages[messageIndex].content;
    const conversationHistory = [];
    let historyCount = 0;
    const maxHistoryPairs = 2;
    
    for (let i = messageIndex - 1; i >= 0 && historyCount < maxHistoryPairs * 2; i--) {
      const msg = messages[i];
      conversationHistory.unshift({
        role: msg.role,
        content: msg.content
      });
      historyCount++;
    }

    const filteredHistory = [];
    let skipNext = false;
    
    for (let i = conversationHistory.length - 1; i >= 0; i--) {
      if (skipNext) {
        skipNext = false;
        continue;
      }
      
      const msg = conversationHistory[i];
      
      if (i === conversationHistory.length - 1 && msg.role === 'user') {
        skipNext = true;
        continue;
      }
      
      filteredHistory.unshift(msg);
    }

    const finalHistory = filteredHistory.slice(-4);

    setVerificationData({
      messageIndex,
      userQuestion,
      llmResponse,
      contextContent: contextContent || '',
      conversationHistory: finalHistory,
      fromCache: false
    });
    setShowVerificationDialog(true);
  }, [messages, verificationCache, contextContent]);

  const handleVerificationComplete = useCallback((messageIndex, verificationResult) => {
    setVerificationCache(prev => {
      const newCache = new Map(prev);
      newCache.set(messageIndex, {
        userQuestion: verificationData.userQuestion,
        llmResponse: verificationData.llmResponse,
        verificationResult: verificationResult
      });
      return newCache;
    });
  }, [verificationData]);

  const handleBackToApp = () => {
    if (originInfo && originInfo.origin === 'pesquisa-jurisprudencia') {
      navigate('/pesquisa-jurisprudencia');
    } else {
      navigate('/clientdata');
    }
  };

  const handleSelectThread = async (thread) => {
    try {
      console.log('🎯 Carregando thread:', thread.id);
      
      const threadMessages = await threadHistory.getThreadMessages(thread.id);
      
      const formattedMessages = threadMessages.map(msg => ({
        id: msg.id.toString(),
        role: msg.role,
        content: msg.content,
        createdAt: msg.timestamp
      }));

      setMessages(formattedMessages);
      setCurrentThreadId(thread.id);
      setThreadId(thread.id);
      
      localStorage.setItem('currentThreadId', thread.id.toString());
      
      setSidebarOpen(false);

      console.log('✅ Thread carregada com', formattedMessages.length, 'mensagens');

    } catch (error) {
      console.error('❌ Erro ao carregar thread:', error);
      alert('Erro ao carregar conversa. Tente novamente.');
    }
  };

  const handleNewConversation = () => {
    console.log('🆕 Nova conversa iniciada');
    
    setCurrentThreadId(null);
    setThreadId(null);
    setMessages([]);
    setContextContent('');
    setBaseContextContent('');
    localStorage.removeItem('currentThreadId');
  };

  const toggleSidebar = () => {
    setSidebarOpen(prev => !prev);
  };

  const isGoogleModel = selectedModel && (
    (selectedModel.provider || '').toLowerCase() === 'google' ||
    (selectedModel.id || '').toLowerCase().includes('gemini') ||
    (selectedModel.id || '').toLowerCase().includes('bard')
  );

  const handleToolAction = useCallback((actionType, data) => {
    console.log('🎬 Tool action:', actionType, data);

    switch (actionType) {
      case 'attach_to_case':
        if (!casoId) {
          alert('Nenhum caso em contexto. Abra este chat a partir de um caso específico.');
          return;
        }
        alert(`Funcionalidade "Anexar ao Caso" será implementada. Caso ID: ${casoId}`);
        break;

      case 'download':
        alert(`Funcionalidade "Download" será implementada. Arquivo: ${data.filename}`);
        break;

      case 'view_caso':
        if (data.casoId) {
          navigate(`/detalhes-caso/${data.casoId}`);
        }
        break;

      default:
        console.warn('Ação desconhecida:', actionType);
    }
  }, [casoId, navigate]);

  const showReasoningToggle = selectedModel && isReasoningSupported(selectedModel.id);
  const showWebSearchCheckbox = selectedModel && isWebSearchSupported(selectedModel.id);
  const showVerbosityCheckbox = selectedModel && isVerbositySupported(selectedModel.id);

  return (
    <div className="chat-ai-container">
      <ThreadSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onSelectThread={handleSelectThread}
        currentThreadId={currentThreadId}
        threadHistory={threadHistory}
      />

      <header className="chat-ai-header">
        <div className="header-left">
          <button 
            className="sidebar-toggle-btn" 
            onClick={toggleSidebar}
            title="Histórico de conversas"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path 
                d="M3 5H17M3 10H17M3 15H17" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round"
              />
            </svg>
          </button>

          <button className="back-btn-minimal" onClick={handleBackToApp} title="Voltar">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M12 4L6 10L12 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          <button
            className="new-conversation-btn"
            onClick={handleNewConversation}
            title="Nova conversa"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path 
                d="M10 4V16M4 10H16" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round"
              />
            </svg>
          </button>
          
          <div className="header-title-section">
            <h1 className="chat-title">Chat com IA</h1>
            {originInfo && (
              <span className="origin-badge">{originInfo.title}</span>
            )}
            {casoId && (
              <span className="caso-badge" title="Caso em contexto">📁 Caso #{casoId}</span>
            )}
            {currentThreadId && (
              <span className="thread-badge" title={`Thread #${currentThreadId}`}>
                💬 Thread #{currentThreadId}
              </span>
            )}
            {codCli ? (
              <span
                className="cliente-badge"
                title={codProcesso ? `Consumo de IA atribuído ao cliente ${codCli}, processo ${codProcesso}` : `Consumo de IA atribuído ao cliente ${codCli}`}
              >
                👤 Cliente {codCli}
                {clienteAtual.nomeCliente ? ` – ${clienteAtual.nomeCliente}` : ''}
                {codProcesso ? ` · Proc. ${codProcesso}` : ''}
              </span>
            ) : (
              <span
                className="cliente-badge cliente-badge-vazio"
                title="O consumo de IA desta conversa não será atribuído a nenhum cliente"
              >
                Sem cliente
              </span>
            )}
          </div>
        </div>
        
        <div className="header-center">
          <div className="model-selector-minimal">
            <svg className="model-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 2L2 6L8 10L14 6L8 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 10L8 14L14 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <select 
              value={selectedModel?.id || ''} 
              onChange={handleModelChange}
              className="model-select-dropdown"
            >
              {models.length === 0 ? (
                <option value="">Carregando modelos...</option>
              ) : (
                <>
                  <option value="">Selecione um modelo...</option>
                  {models.map(model => (
                    <option key={model.id} value={model.id}>
                      {model.name}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>

          {showReasoningToggle && (
            <div className="reasoning-toggle">
              <button 
                className={`toggle-btn ${reasoningMode === 'fast' ? 'active' : ''}`}
                onClick={() => handleReasoningModeChange('fast')}
                title="Modo Rápido - Respostas ágeis (low reasoning)"
              >
                ⚡ Rápido
              </button>
              <button 
                className={`toggle-btn ${reasoningMode === 'deep' ? 'active' : ''}`}
                onClick={() => handleReasoningModeChange('deep')}
                title="Modo Profundo - Raciocínio avançado (high reasoning)"
              >
                🧠 Profundo
              </button>
            </div>
          )}

          {showWebSearchCheckbox && (
            <label className="web-search-checkbox" title="Buscar informações na web">
              <input
                type="checkbox"
                checked={useWebSearch}
                onChange={handleWebSearchChange}
              />
              <span className="checkbox-custom-inline"></span>
              <span className="checkbox-label">🌐 Busca Web</span>
            </label>
          )}

          {showVerbosityCheckbox && (
            <label className="web-search-checkbox" title="Respostas mais longas e detalhadas">
              <input
                type="checkbox"
                checked={useVerbosity}
                onChange={handleVerbosityChange}
              />
              <span className="checkbox-custom-inline"></span>
              <span className="checkbox-label">📝 Respostas Longas</span>
            </label>
          )}
        </div>

        <div className="header-right">
          <button
            className={`attach-btn ${agentRunOpen ? 'active' : ''}`}
            onClick={() => setAgentRunOpen(true)}
            title="Executar agente"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M7 4L16 10L7 16V4Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button
            className={`attach-btn ${attachmentBoxOpen ? 'active' : ''}`}
            onClick={() => setAttachmentBoxOpen(!attachmentBoxOpen)}
            title="Gerenciar anexos"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path 
                d="M17.5 10V14C17.5 15.933 15.933 17.5 14 17.5H6C4.067 17.5 2.5 15.933 2.5 14V10" 
                stroke="currentColor" 
                strokeWidth="1.5" 
                strokeLinecap="round"
              />
              <path 
                d="M10 2.5V12.5M10 12.5L7 9.5M10 12.5L13 9.5" 
                stroke="currentColor" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
            {attachments.anexos.length > 0 && (
              <span className="attach-count">{attachments.anexos.length}</span>
            )}
          </button>

          {hasActiveTools && (
            <span className="tools-indicator" title="Tools ativas">
              🔧
            </span>
          )}
          
          {useWebSearch && (
            <span className="web-search-indicator" title="Busca web ativa">
              🌐
            </span>
          )}

          <button 
            className={`settings-btn-minimal ${showSettings ? 'active' : ''}`}
            onClick={() => setShowSettings(!showSettings)}
            title="Configurações"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M16.25 10C16.25 10.625 16.875 11.25 16.875 11.875C16.875 12.5 16.25 13.125 15.625 13.125L14.375 13.75C13.75 14.375 13.75 15 13.125 15.625C12.5 16.25 11.875 16.25 11.25 15.625L10 14.375C9.375 13.75 8.75 13.75 8.125 14.375L6.875 15.625C6.25 16.25 5.625 16.25 5 15.625C4.375 15 4.375 14.375 3.75 13.75L2.5 13.125C1.875 13.125 1.25 12.5 1.25 11.875C1.25 11.25 1.875 10.625 1.875 10C1.875 9.375 1.25 8.75 1.25 8.125C1.25 7.5 1.875 6.875 2.5 6.875L3.75 6.25C4.375 5.625 4.375 5 5 4.375C5.625 3.75 6.25 3.75 6.875 4.375L8.125 5.625C8.75 6.25 9.375 6.25 10 5.625L11.25 4.375C11.875 3.75 12.5 3.75 13.125 4.375C13.75 5 13.75 5.625 14.375 6.25L15.625 6.875C16.25 6.875 16.875 7.5 16.875 8.125C16.875 8.75 16.25 9.375 16.25 10Z" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
          </button>
        </div>
      </header>

      {/* Dialog de execução de agentes */}
      {agentRunOpen && (
        <AgentRunDialog
          onClose={() => setAgentRunOpen(false)}
          context={buildAgentContext()}
          onAddToConversation={handleAgentResult}
        />
      )}

      {/* ✅ Box de anexos SEM validação de threadId */}
      <AttachmentBox
        isOpen={attachmentBoxOpen}
        onClose={() => setAttachmentBoxOpen(false)}
        anexos={attachments.anexos}
        loading={attachments.loading}
        onAnexarTextoTranscrito={handleAnexarTextoTranscrito}
        onAnexarDocumento={handleAnexarDocumento}
        onAnexarCaso={handleAnexarCaso}
        onAnexarCliente={handleAnexarCliente}
        onRemoverAnexo={attachments.removerAnexo}
      />

      <aside className={`settings-sidebar ${showSettings ? 'open' : ''}`}>
        <div className="settings-sidebar-content">
          <div className="settings-sidebar-header">
            <h3>Configurações</h3>
            <button className="close-settings-btn" onClick={() => setShowSettings(false)}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          <div className="settings-group">
            <div 
              className="settings-group-header"
              onClick={() => setIsSystemPromptCollapsed(!isSystemPromptCollapsed)}
            >
              <span>Prompt do Sistema</span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className={`collapse-icon ${isSystemPromptCollapsed ? '' : 'rotated'}`}>
                <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            {!isSystemPromptCollapsed && (
              <div className="settings-group-content">
                <textarea
                  className="system-prompt-textarea"
                  rows={6}
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  placeholder="Digite instruções para o sistema..."
                />
                <div className="prompt-actions">
                  <button
                    className="save-prompt-button"
                    onClick={handleSaveSystemPrompt}
                    disabled={systemPromptSaving}
                    title="Gravar este prompt na sua conta"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M3 2.5h7L13.5 6v7.5a1 1 0 01-1 1h-9a1 1 0 01-1-1v-10a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                      <path d="M5.5 2.5v4h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {systemPromptSaving ? 'A gravar...' : 'Salvar'}
                  </button>

                  <button
                    className="reset-prompt-button"
                    onClick={() => setSystemPrompt(DEFAULT_SYSTEM_PROMPT)}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M13 3L3 13M3 3L13 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    Restaurar Padrão
                  </button>
                </div>

                {systemPromptStatus && (
                  <span className={`prompt-status ${systemPromptStatus.tipo}`}>
                    {systemPromptStatus.texto}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="settings-group">
            <label className="settings-checkbox">
              <input
                type="checkbox"
                checked={useTools}
                onChange={(e) => setUseTools(e.target.checked)}
              />
              <span className="checkbox-custom"></span>
              <div className="checkbox-label-content">
                <span className="checkbox-label-title">Usar Tools</span>
                <span className="checkbox-label-desc">Permitir que a IA use ferramentas especializadas</span>
              </div>
            </label>
          </div>

          {isGoogleModel && (
            <div className="settings-group google-features">
              <div className="settings-group-title">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M8 5V8L10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                Recursos Google Gemini
              </div>
              
              <label className="settings-checkbox">
                <input
                  type="checkbox"
                  checked={useGroundSearch}
                  onChange={(e) => setUseGroundSearch(e.target.checked)}
                />
                <span className="checkbox-custom"></span>
                <div className="checkbox-label-content">
                  <span className="checkbox-label-title">Ground Search</span>
                  <span className="checkbox-label-desc">Pesquisar informações atualizadas na web</span>
                </div>
              </label>

              <label className="settings-checkbox">
                <input
                  type="checkbox"
                  checked={useThinking}
                  onChange={(e) => setUseThinking(e.target.checked)}
                />
                <span className="checkbox-custom"></span>
                <div className="checkbox-label-content">
                  <span className="checkbox-label-title">Thinking Mode</span>
                  <span className="checkbox-label-desc">Racionalização aprofundada (experimental)</span>
                </div>
              </label>
            </div>
          )}
        </div>
      </aside>

      <main className="chat-ai-main">
        {(baseContextContent || attachments.anexos.length > 0) && (
          <div className={`context-banner ${isContextCollapsed ? 'collapsed' : ''}`}>
            <div 
              className="context-banner-header"
              onClick={() => setIsContextCollapsed(!isContextCollapsed)}
            >
              <div className="context-banner-title">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M4 2H12C13.1046 2 14 2.89543 14 4V12C14 13.1046 13.1046 14 12 14H4C2.89543 14 2 13.1046 2 12V4C2 2.89543 2.89543 2 4 2Z" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M5 6H11M5 9H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                Conteúdo de Contexto
                {attachments.anexos.length > 0 && (
                  <span className="context-attachments-badge">
                    +{attachments.anexos.length} anexo(s)
                  </span>
                )}
              </div>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className={`collapse-icon ${isContextCollapsed ? '' : 'rotated'}`}>
                <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            {!isContextCollapsed && (
              <div className="context-banner-content">
                {baseContextContent && (
                  <div className="context-section">
                    <strong>Contexto Original:</strong>
                    <pre>{baseContextContent}</pre>
                  </div>
                )}
                {attachments.anexos.length > 0 && (
                  <div className="context-section">
                    <strong>Anexos Ativos ({attachments.anexos.length}):</strong>
                    <ul className="context-attachments-list">
                      {attachments.anexos.map(anexo => (
                        <li key={anexo.id}>
                          {anexo.tipo === 'file' && '📄'}
                          {anexo.tipo === 'document' && '📝'}
                          {anexo.tipo === 'case' && '📁'}
                          {anexo.tipo === 'client' && '👤'}
                          {' '}
                          {anexo.preview} ({anexo.word_count} palavras)
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="messages-area" ref={messagesAreaRef}>
          {messages.length === 0 && !isLoading && (
            <div className="empty-state">
              <div className="empty-state-icon">
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                  <circle cx="32" cy="32" r="30" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4" opacity="0.3"/>
                  <path d="M32 20V32L40 36" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h2>Pronto para conversar</h2>
              <p>Envie uma mensagem para começar a interagir com o assistente jurídico.</p>
              {contextContent && (
                <p className="context-hint">O conteúdo carregado será incluído automaticamente nas suas consultas.</p>
              )}
              {useTools && (
                <p className="tools-hint">🔧 Tools habilitadas: A IA pode usar ferramentas especializadas para ajudá-lo.</p>
              )}
              {showReasoningToggle && (
                <p className="reasoning-hint">
                  {reasoningMode === 'fast' ? '⚡ Modo Rápido ativado' : '🧠 Modo Profundo ativado'}
                </p>
              )}
              {useWebSearch && (
                <p className="web-search-hint">🌐 Busca web habilitada - respostas com fontes da internet</p>
              )}
            </div>
          )}
          
          {messages.map((message, index) => (
            <ChatMessage
              key={message.id || `msg-${index}`}
              message={message}
              selectedModelId={selectedModel?.id}
              onVerify={message.role === 'assistant' && !message.error ? () => handleVerifyResponse(index) : null}
              isVerified={verificationCache.has(index)}
              toolCalls={message.toolCalls}
              toolResults={toolResults}
              onToolAction={handleToolAction}
            />
          ))}
          
          {isLoading && (
            <div className="message-loading">
              <div className="message-loading-avatar">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="message-loading-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                {hasActiveTools && (
                  <span className="loading-tools-hint">Executando ferramentas...</span>
                )}
                {showReasoningToggle && reasoningMode === 'deep' && (
                  <span className="loading-reasoning-hint">🧠 Pensando profundamente...</span>
                )}
                {useWebSearch && (
                  <span className="loading-web-search-hint">🌐 Pesquisando na web...</span>
                )}
              </div>
            </div>
          )}

          {chatError && (
            <div className="message error">
              <div className="message-header">
                <span className="message-author">Erro</span>
              </div>
              <div className="message-content">
                <p className="error-message-text">❌ {chatError.message || 'Ocorreu um erro inesperado'}</p>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        <div className="input-area">
          <div className="input-container">
            
            
            <textarea
              ref={inputRef}
              className="message-textarea"
              value={input}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder="Digite sua mensagem..."
              rows={1}
              disabled={isLoading || !selectedModel}
            />
            
            <button 
              className={`send-message-btn ${input.trim() && !isLoading ? 'active' : ''}`}
              onClick={handleSendMessage}
              disabled={!input.trim() || isLoading || !selectedModel}
              title={!selectedModel ? 'Selecione um modelo primeiro' : 'Enviar mensagem'}
            >
              {isLoading ? (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="loading-spin">
                  <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2" opacity="0.25"/>
                  <path d="M10 2C5.58172 2 2 5.58172 2 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M18 10L2 2L5 10L2 18L18 10Z" fill="currentColor"/>
                </svg>
              )}
            </button>
          </div>
          <div className="input-hint">
            <span>Pressione Enter para enviar, Shift + Enter para nova linha</span>
          </div>
        </div>
      </main>

      {showVerificationDialog && verificationData && (
        <VerificationDialog
          isOpen={showVerificationDialog}
          onClose={() => setShowVerificationDialog(false)}
          userQuestion={verificationData.userQuestion}
          llmResponse={verificationData.llmResponse}
          contextContent={verificationData.contextContent}
          conversationHistory={verificationData.conversationHistory}
          cachedResult={verificationData.verificationResult}
          fromCache={verificationData.fromCache}
          onVerificationComplete={(result) => 
            handleVerificationComplete(verificationData.messageIndex, result)
          }
        />
      )}
    </div>
  );
};

export default ChatWithAI;